/**
 * Level Loader — parse and load custom level JSON files.
 * Used with ?level=<filename> URL param to bypass procedural generation.
 * Supports v1/v2 (legacy .json) and v3 (.gbstage) formats.
 */

import { importGridBase64 } from '../terrain/grid';
import type { StageTheme } from '../stages/registry';
import { getThemeById } from '../stages/registry';

export interface LevelData {
  version: number;
  name: string;
  author: string;
  stageId?: string;
  stage: { left: number; right: number };
  spawns: { x: number; y: number }[];
  gridData: string;
  /** v3 only: partial theme overrides to merge with base theme */
  themeOverrides?: Record<string, unknown>;
  /** v3 only: base theme ID for override merging */
  baseThemeId?: string;
}

/** Module-level ref — set on first load, used by reset.ts to reload on round reset. */
let activeLevel: LevelData | null = null;

export function getActiveLevel(): LevelData | null {
  return activeLevel;
}

export function setActiveLevel(level: LevelData | null): void {
  activeLevel = level;
}

/** Validate and parse raw JSON into a LevelData. Handles v1/v2 and v3 formats. */
export function parseLevelJSON(json: unknown): LevelData {
  const obj = json as Record<string, unknown>;
  if (!obj || typeof obj !== 'object') throw new Error('Level JSON is not an object');

  // v3 format detection
  if (obj.format === 'geometry-blast-stage') {
    return parseV3(obj);
  }

  // v1/v2 legacy format
  return parseLegacy(obj);
}

/** Parse v3 .gbstage format */
function parseV3(obj: Record<string, unknown>): LevelData {
  if (typeof obj.gridData !== 'string') throw new Error('Level missing gridData string');

  const bounds = obj.bounds as { stageLeft?: number; stageRight?: number } | undefined;
  if (!bounds || typeof bounds.stageLeft !== 'number' || typeof bounds.stageRight !== 'number') {
    throw new Error('Level missing bounds.stageLeft/stageRight');
  }

  const meta = (obj.meta || {}) as Record<string, unknown>;
  const theme = (obj.theme || {}) as Record<string, unknown>;
  const spawns = Array.isArray(obj.spawns) ? obj.spawns : [];
  const baseThemeId = typeof theme.baseTheme === 'string' ? theme.baseTheme : 'neon_void';

  // Deserialize hex string overrides → numbers
  const rawOverrides = (theme.overrides || {}) as Record<string, unknown>;
  const overrides: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(rawOverrides)) {
    if (typeof val === 'string' && val.startsWith('0x')) {
      overrides[key] = parseInt(val, 16);
    } else if (Array.isArray(val)) {
      overrides[key] = [...val];
    } else {
      overrides[key] = val;
    }
  }

  return {
    version: typeof obj.version === 'number' ? obj.version : 3,
    name: typeof meta.name === 'string' ? meta.name : 'Untitled',
    author: typeof meta.author === 'string' ? meta.author : '',
    stageId: baseThemeId,
    stage: { left: bounds.stageLeft, right: bounds.stageRight },
    spawns: spawns.map((s: { x: number; y: number }) => ({ x: s.x, y: s.y })),
    gridData: obj.gridData as string,
    baseThemeId,
    themeOverrides: Object.keys(overrides).length > 0 ? overrides : undefined,
  };
}

/** Parse v1/v2 legacy format */
function parseLegacy(obj: Record<string, unknown>): LevelData {
  if (typeof obj.gridData !== 'string') throw new Error('Level missing gridData string');
  if (!obj.stage || typeof (obj.stage as Record<string, unknown>).left !== 'number')
    throw new Error('Level missing stage.left');
  if (typeof (obj.stage as Record<string, unknown>).right !== 'number')
    throw new Error('Level missing stage.right');

  const spawns = Array.isArray(obj.spawns) ? obj.spawns : [];

  return {
    version: typeof obj.version === 'number' ? obj.version : 1,
    name: typeof obj.name === 'string' ? obj.name : 'Untitled',
    author: typeof obj.author === 'string' ? obj.author : '',
    stageId: typeof obj.stageId === 'string' ? obj.stageId : undefined,
    stage: {
      left: (obj.stage as { left: number; right: number }).left,
      right: (obj.stage as { left: number; right: number }).right,
    },
    spawns: spawns.map((s: { x: number; y: number }) => ({ x: s.x, y: s.y })),
    gridData: obj.gridData as string,
  };
}

/**
 * Merge theme overrides onto a base theme.
 * Returns a new StageTheme with overrides applied.
 */
export function mergeThemeOverrides(baseThemeId: string, overrides: Record<string, unknown>): StageTheme {
  const base = getThemeById(baseThemeId);
  const merged = { ...base };
  for (const [key, val] of Object.entries(overrides)) {
    if (key in base) {
      if (Array.isArray(val)) {
        (merged as Record<string, unknown>)[key] = [...val];
      } else {
        (merged as Record<string, unknown>)[key] = val;
      }
    }
  }
  return merged;
}

/** Load a level into the terrain grid. Returns spawn positions. */
export function loadLevel(level: LevelData): { spawnXs: number[]; spawnYs: number[]; stageLeft: number; stageRight: number } {
  importGridBase64(level.gridData);
  activeLevel = level;
  return {
    spawnXs: level.spawns.map(s => s.x),
    spawnYs: level.spawns.map(s => s.y),
    stageLeft: level.stage.left,
    stageRight: level.stage.right,
  };
}
